import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { axiosPost } from '../../api/axios';
import { ToggleButton } from '../common';
import { Client, useListClients } from '../../hooks/useClients';

export type PreviewDomainKey = 'kindlyhuman' | 'kindlychristian';

export interface EmailPreviewRequest {
  template_name: string;
  domain_key: PreviewDomainKey;
  powered_by_enabled: boolean;
  message_body?: string;
  subject?: string;
  client_id?: number;
}

export interface EmailPreviewResponse {
  html: string;
  template_name: string;
  domain_key: string;
  powered_by_enabled: boolean;
  client_id?: number | null;
  client_name?: string | null;
  subject_preview?: string | null;
  message_preview?: string | null;
  merge_vars: Record<string, string>;
}

const postEmailPreview = async (payload: EmailPreviewRequest): Promise<EmailPreviewResponse> => {
  const response = await axiosPost('/comms/email_templates/preview', payload, 'v3');
  return response.data;
};

type EmailPreviewModalProps = {
  templateName: string;
  subject?: string;
  messageBody?: string;
  initialDomainKey?: PreviewDomainKey;
  /** Optional alternate Mandrill slugs (e.g. weekly label → several templates). */
  templateOptions?: string[];
  onClose: () => void;
};

const EmailPreviewModal: React.FunctionComponent<EmailPreviewModalProps> = ({
  templateName,
  subject,
  messageBody,
  initialDomainKey = 'kindlyhuman',
  templateOptions,
  onClose,
}) => {
  const options = templateOptions && templateOptions.length > 0 ? templateOptions : templateName ? [templateName] : [];
  const [selectedTemplate, setSelectedTemplate] = useState(options[0] || templateName);
  const [domainKey, setDomainKey] = useState<PreviewDomainKey>(initialDomainKey);
  const [poweredBy, setPoweredBy] = useState(false);
  const [clientId, setClientId] = useState<number | ''>('');
  const [activeTab, setActiveTab] = useState<'email' | 'copy'>('email');

  const { data: clients } = useListClients();

  const clientOptions = useMemo(() => {
    const list = (clients ?? []).filter((c: Client) => c.id != null && !c.archived_at);
    return [...list].sort((a, b) => {
      const aPb = a.powered_by_header_enabled ? 0 : 1;
      const bPb = b.powered_by_header_enabled ? 0 : 1;
      if (aPb !== bPb) return aPb - bPb;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [clients]);

  const selectedClient = useMemo(() => clientOptions.find((c) => c.id === clientId), [clientOptions, clientId]);

  const previewMutation = useMutation((payload: EmailPreviewRequest) => postEmailPreview(payload));

  const runPreview = () => {
    if (!selectedTemplate || selectedTemplate === 'No Template') return;
    previewMutation.mutate({
      template_name: selectedTemplate,
      domain_key: domainKey,
      powered_by_enabled: poweredBy,
      message_body: messageBody || undefined,
      subject: subject || undefined,
      client_id: typeof clientId === 'number' ? clientId : undefined,
    });
  };

  useEffect(() => {
    runPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, domainKey, poweredBy, clientId]);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black opacity-25" aria-hidden="true" />
      <div
        className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden p-4 outline-none focus:outline-none"
        onClick={onClose}
      >
        <div
          className="relative flex h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-lg"
          role="dialog"
          aria-modal="true"
          aria-labelledby="email-preview-title"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="border-b border-gray-200 px-6 py-4">
            <div id="email-preview-title" className="blue-primary text-2xl">
              Email preview
            </div>
            <p className="text-gray-dark mt-1 text-sm">
              Sample merge vars only — does not send. Pick a client to preview Powered By header / group logos. Domain
              chrome still depends on Mandrill using merge vars (KINDLY-1142).
            </p>
            <div className="mt-4 flex flex-wrap items-end gap-4">
              {options.length > 1 && (
                <label className="text-sm">
                  <span className="text-gray-dark mb-1 block text-xs font-semibold uppercase">Template</span>
                  <select
                    className="border-gray-light rounded border px-2 py-1 text-sm"
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                  >
                    {options.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={
                    'rounded-full px-4 py-1 text-sm ' +
                    (domainKey === 'kindlyhuman' ? 'bg-blue-primary font-bold text-white' : 'bg-gray-100')
                  }
                  onClick={() => setDomainKey('kindlyhuman')}
                >
                  Kindly Human
                </button>
                <button
                  type="button"
                  className={
                    'rounded-full px-4 py-1 text-sm ' +
                    (domainKey === 'kindlychristian' ? 'bg-blue-primary font-bold text-white' : 'bg-gray-100')
                  }
                  onClick={() => setDomainKey('kindlychristian')}
                >
                  Kindly Christian
                </button>
              </div>
              <label className="text-sm">
                <span className="text-gray-dark mb-1 block text-xs font-semibold uppercase">Client sample</span>
                <select
                  className="border-gray-light max-w-xs rounded border px-2 py-1 text-sm"
                  value={clientId === '' ? '' : String(clientId)}
                  onChange={(e) => {
                    const value = e.target.value;
                    setClientId(value ? Number(value) : '');
                  }}
                >
                  <option value="">Generic sample (no client assets)</option>
                  {clientOptions.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                      {client.powered_by_header_enabled ? ' · Powered By' : ''}
                      {client.powered_by_header_file_url ? ' · header' : ''}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex items-center gap-2">
                <ToggleButton
                  id="preview-powered-by"
                  status={poweredBy}
                  setStatus={setPoweredBy}
                  initialstatus={true}
                />
                <span className="text-sm font-medium">Powered By sample</span>
              </div>
              <div className="ml-auto flex gap-2">
                <button
                  type="button"
                  className={
                    'rounded-full px-3 py-1 text-sm ' +
                    (activeTab === 'email' ? 'bg-blue-primary text-white' : 'bg-gray-100')
                  }
                  onClick={() => setActiveTab('email')}
                >
                  HTML
                </button>
                <button
                  type="button"
                  className={
                    'rounded-full px-3 py-1 text-sm ' +
                    (activeTab === 'copy' ? 'bg-blue-primary text-white' : 'bg-gray-100')
                  }
                  onClick={() => setActiveTab('copy')}
                >
                  Subject / body
                </button>
              </div>
            </div>
            {poweredBy && !selectedClient?.powered_by_header_file_url && (
              <p className="mt-2 text-xs text-amber-800">
                Powered By is on, but no header image is available
                {selectedClient
                  ? ` for ${selectedClient.name}`
                  : ' — choose a client that has a Powered By header uploaded'}
                . Templates fall back to the domain logo branch.
              </p>
            )}
          </div>

          <div className="flex-1 overflow-hidden bg-gray-50 p-4">
            {previewMutation.isLoading && (
              <div className="text-gray-dark py-10 text-center text-sm">Rendering Mandrill template…</div>
            )}
            {previewMutation.isError && (
              <div className="py-6 text-center text-sm text-red-600">
                Could not render preview. Check the template slug and that Mandrill is reachable.
              </div>
            )}
            {!previewMutation.isLoading && previewMutation.data && activeTab === 'email' && (
              <iframe
                title="Mandrill email preview"
                className="h-full w-full rounded border border-gray-200 bg-white"
                sandbox=""
                srcDoc={previewMutation.data.html}
              />
            )}
            {!previewMutation.isLoading && previewMutation.data && activeTab === 'copy' && (
              <div className="mx-auto max-w-2xl space-y-4 overflow-y-auto rounded border border-gray-200 bg-white p-6">
                <div>
                  <div className="text-gray-dark text-xs font-semibold uppercase">Subject</div>
                  <p className="mt-1 text-base font-medium">
                    {previewMutation.data.subject_preview || subject || '(no subject)'}
                  </p>
                </div>
                <div>
                  <div className="text-gray-dark text-xs font-semibold uppercase">
                    Message body (after local replace)
                  </div>
                  <pre className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-800">
                    {previewMutation.data.message_preview || messageBody || '(empty)'}
                  </pre>
                </div>
                <p className="text-gray-dark text-xs">
                  Subject / body use the same sample vars as the HTML render. SMS / push / in-app see this copy path;
                  Mandrill HTML is on the HTML tab.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-center border-t border-gray-200 py-3">
            <button type="button" className="bg-gray-light rounded-full px-7 py-2 text-sm text-white" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailPreviewModal;
